/**
 * Diagnosis history.
 *
 * Free-plan rows carry an expiry and are removed by the scheduled cleanup, so
 * the table states the retention rule instead of implying persistence.
 */

import { useState } from 'react';
import type { QuotaStatus } from '@core/types';
import { Alert, Badge, EmptyState, KindBadge, QuotaMeter, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { api, type HistorySummary } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { formatDateTime, formatRelative, formatTitleCase, pluralise } from '../lib/format';
import { Link } from '../lib/router';

const PAGE_SIZE = 25;

export interface HistoryProps {
  quota: QuotaStatus;
}

export function HistoryPage({ quota }: HistoryProps) {
  const [offset, setOffset] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const state = useAsync(async (token) => api.listDiagnoses(token, PAGE_SIZE, offset), [offset]);

  return (
    <>
      <PageHead
        title="Diagnosis history"
        description={
          state.data?.retention.persistent
            ? 'History is kept until you delete it or delete your account.'
            : `Free-plan history expires ${state.data?.retention.retentionDays ?? 7} days after creation. Export anything you need to keep.`
        }
        actions={<QuotaMeter quota={quota} />}
      />

      {state.loading ? (
        <div className="card row">
          <Spinner label="Loading history" /> <span>Loading history…</span>
        </div>
      ) : null}

      {state.error ? (
        <Alert tone="error" title="History could not be loaded">
          <p>{state.error.message}</p>
          <button type="button" className="button--small" onClick={state.reload}>
            Try again
          </button>
        </Alert>
      ) : null}

      {state.data && state.data.diagnoses.length === 0 ? (
        <EmptyState title="No diagnoses yet">
          <p>
            Paste a failing request or response on the <Link to="/app">new diagnosis</Link> page and
            it will appear here.
          </p>
        </EmptyState>
      ) : null}

      {state.data && state.data.diagnoses.length > 0 ? (
        <>
          <div className="table-wrap">
            <table>
              <caption className="sr-only">Diagnosis history, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">Created</th>
                  <th scope="col">Input</th>
                  <th scope="col">Summary</th>
                  <th scope="col">Top cause</th>
                  <th scope="col">Findings</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.data.diagnoses.map((row: HistorySummary) => (
                  <tr key={row.id}>
                    <td className="nowrap">
                      <div>{formatDateTime(row.createdAt)}</div>
                      <div className="small faint">{formatRelative(row.createdAt)}</div>
                      {row.expiresAt ? (
                        <div className="small faint">expires {formatRelative(row.expiresAt)}</div>
                      ) : null}
                    </td>
                    <td>
                      <code className="small">{formatTitleCase(row.detectedInputType)}</code>
                    </td>
                    <td style={{ maxWidth: 380 }}>
                      <span className="small">{row.summary || '—'}</span>
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      {row.topCause ? (
                        <div className="stack stack--tight">
                          <span className="small">{row.topCause.title}</span>
                          <span className="row" style={{ gap: 6 }}>
                            <KindBadge
                              kind={row.topCause.kind as 'confirmed' | 'likely' | 'speculative'}
                            />
                            <span className="small faint">{row.topCause.confidence}%</span>
                          </span>
                        </div>
                      ) : (
                        <span className="faint small">None identified</span>
                      )}
                    </td>
                    <td className="nowrap small faint">
                      {row.counts.confirmed} / {row.counts.likely} / {row.counts.speculative}
                    </td>
                    <td>
                      <Link className="button button--small" to={`/app/diagnoses/${row.id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row row--between" style={{ marginTop: 16 }}>
            <div className="row">
              <span className="small faint">
                Showing {offset + 1}–{offset + state.data.diagnoses.length}
              </span>
            </div>
            <div className="row">
              <button
                type="button"
                className="button--small"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Newer
              </button>
              <button
                type="button"
                className="button--small"
                disabled={state.data.diagnoses.length < PAGE_SIZE}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Older
              </button>
            </div>
          </div>

          <details style={{ marginTop: 16 }}>
            <summary>How history is stored and expired</summary>
            <ul className="plain-list">
              <li>Only the redacted text is stored, never your original paste.</li>
              <li>
                {state.data.retention.persistent
                  ? 'Pro history is persistent until you delete it.'
                  : `Free history expires after ${state.data.retention.retentionDays} ${pluralise(state.data.retention.retentionDays ?? 7, 'day')} and is removed by a daily cleanup job.`}
              </li>
              <li>Deleting a diagnosis also removes any share links created from it.</li>
              <li>
                Every row is owned by your account; the API refuses queries for other accounts and
                Row Level Security enforces the same rule at the database level.
              </li>
            </ul>
          </details>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="button--ghost button--small"
              onClick={() => setShowAll((value) => !value)}
              aria-expanded={showAll}
            >
              {showAll ? 'Hide' : 'Show'} what each column means
            </button>
          </div>
          {showAll ? (
            <ul className="plain-list small">
              <li>
                <strong>Findings</strong>: counts of confirmed / likely / speculative items in the
                stored report.
              </li>
              <li>
                <strong>Top cause</strong>: the highest-ranked cause at the time the report was
                generated; it is a snapshot, not a live re-evaluation.
              </li>
            </ul>
          ) : null}
        </>
      ) : null}

      {state.data ? (
        <p className="small faint" style={{ marginTop: 16 }}>
          <Badge>{state.data.diagnoses.length}</Badge> diagnoses on this page
        </p>
      ) : null}
    </>
  );
}
